import { useEffect, useState } from 'react'
import { Phone, Copy, Check } from 'lucide-react'
import { telephonyApi } from '../../services/telephony'

interface DialInChipProps {
  channelId: string
}

// Format an E.164 phone number for display. NANP (+1 XXX XXX XXXX) gets
// pretty-printed; all other numbers fall back to the raw string.
function formatE164(number: string): string {
  const nanp = number.match(/^\+1(\d{3})(\d{3})(\d{4})$/)
  if (nanp) return `+1 ${nanp[1]} ${nanp[2]} ${nanp[3]}`
  return number
}

export function DialInChip({ channelId }: DialInChipProps) {
  const [ext, setExt] = useState<{ extension: string; mainNumber: string | null; directNumber: string | null } | null>(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    telephonyApi.channels.getExtension(channelId).then(setExt).catch(() => {
      // Render nothing on error — telephony may not be configured for this channel.
    })
  }, [channelId])

  // Render nothing while loading, on error, or when no number is configured.
  if (!ext || (!ext.directNumber && !ext.mainNumber)) return null

  // A dedicated channel number routes callers straight into the lobby, so it
  // is shown alone — no room extension needed.
  const direct = ext.directNumber
  const displayNumber = formatE164(direct ?? ext.mainNumber!)
  // Extension displayed grouped: "123 456"
  const displayExt = `${ext.extension.slice(0, 3)} ${ext.extension.slice(3)}`
  const copyText = direct ? `Call ${direct}` : `Call ${ext.mainNumber}, room ${ext.extension}`

  function handleCopy() {
    navigator.clipboard.writeText(copyText).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }).catch(() => {})
  }

  return (
    <div className="flex items-center gap-1.5 bg-muted rounded-md px-2 py-1 text-xs text-muted-foreground">
      <Phone size={13} className="shrink-0" />
      <span>
        {displayNumber}
        {!direct && <> &middot; Room {displayExt}</>}
      </span>
      <button
        title={copied ? 'Copied!' : 'Copy dial-in info'}
        onClick={handleCopy}
        className="ml-0.5 hover:text-foreground transition-colors duration-150"
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
      </button>
    </div>
  )
}
